/** Waste reduction actions tab - recommended actions with owner, scope, due date, priority,
 * estimated saving/carbon reduction, status, evidence and result after completion. Status can
 * be advanced locally (not started -> in progress -> completed) to demonstrate the workflow;
 * not persisted to any backend (see header comment in `data.ts`). */

import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { WasteActionItem } from '../types';
import { StatusChip } from './StatusChip';

interface ActionsTabProps {
  actions: WasteActionItem[];
  currencyCode?: string;
  onAdvanceStatus: (id: string) => void;
}

const NEXT_STATUS: Record<WasteActionItem['status'], WasteActionItem['status'] | null> = {
  'Not started': 'In progress',
  'In progress': 'Completed',
  Overdue: 'In progress',
  Completed: null,
};

const STATUS_TO_SEVERITY: Record<WasteActionItem['status'], 'good' | 'warning' | 'critical'> = {
  'Not started': 'warning',
  'In progress': 'warning',
  Overdue: 'critical',
  Completed: 'good',
};

const ActionsTab = ({ actions, currencyCode, onAdvanceStatus }: ActionsTabProps) => {
  const { t } = useTranslation('common');
  const totalEstimatedSaving = actions.reduce((sum, a) => (a.status === 'Completed' ? sum : sum + a.estimatedSaving), 0);
  const totalCarbonReduction = actions.reduce((sum, a) => (a.status === 'Completed' ? sum : sum + a.estimatedCarbonReductionKg), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4, display: 'flex', gap: 6 }}>
          <Box>
            <Typography variant="textXS"
              color="text.secondary">{t('wasteDashboardOpenSavingOpportunity', 'Open savings opportunity')}</Typography>
            <Typography variant="textLG"
              fontWeight={600}>{applyRoundAndFormat(totalEstimatedSaving, currencyCode)}</Typography>
          </Box>
          <Box>
            <Typography variant="textXS"
              color="text.secondary">{t('wasteDashboardOpenCarbonReduction', 'Open carbon reduction opportunity')}</Typography>
            <Typography variant="textLG"
              fontWeight={600}>{(totalCarbonReduction / 1000).toFixed(2)} tCO2e</Typography>
          </Box>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardActionsTitle', 'Waste reduction actions')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardAction', 'Recommended action')}</TableCell>
                <TableCell>{t('wasteDashboardOwner', 'Owner')}</TableCell>
                <TableCell>{t('wasteDashboardScope', 'Site / supplier')}</TableCell>
                <TableCell>{t('wasteDashboardDueDate', 'Due date')}</TableCell>
                <TableCell>{t('wasteDashboardPriority', 'Priority')}</TableCell>
                <TableCell align="right">{t('wasteDashboardEstimatedSaving', 'Est. saving')}</TableCell>
                <TableCell align="right">{t('wasteDashboardEstimatedCarbonReduction', 'Est. carbon reduction')}</TableCell>
                <TableCell>{t('wasteDashboardStatus', 'Status')}</TableCell>
                <TableCell>{t('wasteDashboardEvidence', 'Evidence / notes')}</TableCell>
                <TableCell>{t('wasteDashboardResult', 'Result after completion')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {actions.map((action) => {
                const nextStatus = NEXT_STATUS[action.status];
                return (
                  <TableRow key={action.id}>
                    <TableCell>{action.action}</TableCell>
                    <TableCell>{action.owner}</TableCell>
                    <TableCell>{action.scope}</TableCell>
                    <TableCell>{action.dueDate}</TableCell>
                    <TableCell><StatusChip status={action.priority}
                      label={action.priority === 'critical' ? t('wasteDashboardCritical', 'Critical') : action.priority === 'warning' ? t('wasteDashboardWarning', 'Warning') : t('wasteDashboardLow', 'Low')} /></TableCell>
                    <TableCell align="right">{applyRoundAndFormat(action.estimatedSaving, currencyCode)}</TableCell>
                    <TableCell align="right">{action.estimatedCarbonReductionKg} kg</TableCell>
                    <TableCell><StatusChip status={STATUS_TO_SEVERITY[action.status]}
                      label={action.status} /></TableCell>
                    <TableCell>{action.evidence}</TableCell>
                    <TableCell>{action.resultAfterCompletion ?? '\u2013'}</TableCell>
                    <TableCell>
                      {nextStatus && (
                        <Button size="md"
                          variant="secondary"
                          onClick={() => onAdvanceStatus(action.id)}>
                          {t('wasteDashboardMarkAs', 'Mark as')} {nextStatus}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </Box>
  );
};

export default ActionsTab;
